import type { MouseEvent, ReactNode } from 'react';

import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';

import { Image } from '../../../../libraries/design/components/image/src/image';
import { iconMapping } from '../../../../uniform/components/text-with-icon/src/icon-mapping';

import { CustomCheckbox } from './inputs/custom-checkbox';
import { CustomRadio } from './inputs/custom-radio';

import type { BulletOrIconListItem } from './strings';

export type ChoiceCardProps = {
	value: string;
	selected: boolean;
	label?: string;
	hide?: boolean;
	inputType?: 'radio' | 'checkbox';
	onSelect: () => void;
	description?: string | ReactNode;
	usageInfo?: string;
	list?: BulletOrIconListItem[];
	info?: string | ReactNode;
	/** URL for the product icon mark (shown next to heading). */
	iconUrl?: string;
	/** URL for the collection lockup logo (shown below heading). */
	lockupUrl?: string;
	/** Used only for grouping the underlying input elements */
	groupName?: string;
};

/* eslint-disable complexity */
export const ChoiceCard = ({
	value,
	selected,
	onSelect,
	hide = false,
	inputType = 'radio',
	label,
	description,
	usageInfo,
	list,
	info,
	iconUrl,
	lockupUrl,
	groupName,
}: ChoiceCardProps) => {
	const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		onSelect();
	};

	const heading = label;

	if (!hide) {
		return (
			<div css={[cardStyle, selected && cardSelectedStyle]}>
				<div css={radioOrCheckContainer}>
					{inputType === 'checkbox' ? (
						<CustomCheckbox checked={selected} onChange={onSelect} />
					) : (
						<CustomRadio
							checked={selected}
							name={groupName ?? `group-${value}`}
							value={String(value)}
							onChange={onSelect}
						/>
					)}
				</div>

				<div css={textContainer}>
					{heading && (
						<div css={headingContainer} className={`icon_${iconUrl ? 'true' : 'false'}`}>
							{iconUrl && <Image src={iconUrl} alt="" css={iconImageStyle} />}
							<h3 css={cardHeading}>
								<span css={cardHeadingSpan}>{heading}</span>
							</h3>
						</div>
					)}

					{lockupUrl && <Image src={lockupUrl} alt="" css={lockupImageStyle} />}

					<div css={cardDescription}>{description}</div>

					{usageInfo && <div css={usageInfoStyle}>{usageInfo}</div>}

					{list && list.length > 0 && (
						<ul css={listContainer}>
							{list.map((listItem: BulletOrIconListItem, index: number) => {
								const IconMapped = listItem.icon
									? iconMapping[listItem.icon]?.component
									: undefined;
								const hasIcon = Boolean(IconMapped);
								return (
									<li
										css={[listElement, hasIcon && listElementWithIcon]}
										key={`${value}-list-${index}`}
									>
										{hasIcon && IconMapped && (
											<span css={iconStyle}>
												<IconMapped size="small" />
											</span>
										)}
										{listItem.text}
									</li>
								);
							})}
						</ul>
					)}
				</div>

				{info && <div css={infoBoxStyle}>{info}</div>}

				<button
					type="button"
					css={[selectorOverlayStyle]}
					onClick={handleClick}
					role={inputType}
					aria-checked={selected}
					aria-label={typeof heading === 'string' ? heading : value}
				/>
			</div>
		);
	}
	return null;
};
/* eslint-enable complexity */

const cardStyle = css({
	position: 'relative',
	flex: '1 1 200px',
	borderWidth: '1px',
	borderStyle: 'solid',
	borderColor: wacToken('color.neutral.light300'),
	borderRadius: wacToken('corner.radius.card'),
	backgroundColor: wacToken('color.neutral.light0'),
	color: wacToken('color.neutral.light1100'),
	cursor: 'pointer',
	display: 'flex',
	gap: token('space.200'),
	flexDirection: 'column',
	textAlign: 'left',
	alignItems: 'flex-start',
	justifyContent: 'flex-start',
	boxSizing: 'border-box',
});

const cardSelectedStyle = css({
	borderColor: wacToken('color.primary.blue'),
	outline: `2px solid ${wacToken('color.primary.blue')}`,
});

const selectorOverlayStyle = css({
	appearance: 'none',
	border: 0,
	borderRadius: 'inherit',
	background: 'none',
	position: 'absolute',
	inset: '-1px',
	cursor: 'pointer',
	[`&:focus-visible`]: {
		outline: `3px solid ${wacToken('color.primary.blue')}`,
		outlineOffset: '-3px',
	},
});

const radioOrCheckContainer = css({
	padding: `${token('space.200')} 0 0 ${token('space.250')}`,
});

const textContainer = css({
	padding: `0 ${token('space.250')} ${token('space.250')}`,
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	gap: token('space.250'),
});

const headingContainer = css({
	['&.icon_true']: {
		display: 'grid',
		alignItems: 'center',
		gridTemplateColumns: '48px 1fr',
		gap: token('space.200'),
	},
});

const cardHeading = css({
	fontFamily: wacToken('font.charlie.display'),
	lineHeight: 1,
	margin: 0,
	fontWeight: 600,
});

const cardHeadingSpan = css({
	fontSize: '1.5rem',
	lineHeight: 1,
	[media.below.md]: {
		fontSize: '1.375rem',
	},
});

const cardDescription = css({
	fontFamily: wacToken('font.charlie.text'),
	fontSize: token('font.heading.medium'),
	margin: `0 0 ${token('space.100')}`,
});

const usageInfoStyle = css({
	fontWeight: 700,
	fontFamily: wacToken('font.charlie.text'),
	fontSize: token('font.heading.medium'),
	margin: `0 0 ${token('space.100')}`,
});

const listContainer = css({
	padding: `0 0 0 ${token('space.300')}`,
	margin: 0,
});

const listElement = css({
	fontFamily: wacToken('font.charlie.text'),
	fontSize: token('font.heading.medium'),
	margin: `0 0 ${token('space.150')}`,
	position: 'relative',
});

const listElementWithIcon = css({
	listStyleType: 'none',
});

const iconImageStyle = css({
	display: 'block',
	width: '48px',
	height: '48px',
	objectFit: 'contain',
});

const lockupImageStyle = css({
	display: 'block',
	height: '48px',
	width: 'auto',
	objectFit: 'contain',
});

const iconStyle = css({
	color: wacToken('color.extended.blue700'),
	position: 'absolute',
	insetInlineStart: token('space.negative.250'),
	minWidth: '45px',
});

const infoBoxStyle = css({
	marginBlockStart: 'auto',
	width: '100%',
	backgroundColor: wacToken('color.extended.blue100'),
	borderRadius: `0 0 ${wacToken('corner.radius.card')} ${wacToken('corner.radius.card')}`,
	padding: token('space.150'),
	fontFamily: wacToken('font.charlie.text'),
	fontSize: token('font.heading.medium'),
	textAlign: 'center',
});
